import React from 'react';
import styled from 'styled-components';
import {
  Box,
  Dialog as MuiDialog,
  DialogActions,
  DialogContent,
  DialogProps as MuiDialogProps,
  DialogTitle,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';
import { Transition } from '../../shared/Transition/TransitionUp';

export type DialogProps = {
  open: boolean;
  onClose: () => void;
  /** Heading content — the close button comes with the dialog. */
  title: React.ReactNode;
  /** The footer buttons, in reading order; the bar stacks them on phones. */
  actions: React.ReactNode;
  /** Forwarded to MUI — e.g. to reset a form on `onExited`. */
  TransitionProps?: MuiDialogProps['TransitionProps'];
  maxWidth?: MuiDialogProps['maxWidth'];
  children: React.ReactNode;
};

/**
 * The app's standard dialog shell: responsive sizing, a heading row with a close button, a scrollable
 * body, and a footer that stacks its buttons on phones. Callers supply only the content — generalized
 * from the out-of-stock dialogs, see docs/design-system/components.md.
 */
export const Dialog: React.FC<DialogProps> = ({
  open,
  onClose,
  title,
  actions,
  TransitionProps,
  maxWidth = 'sm',
  children,
}) => {
  const dialogProps = useResponsiveDialogProps(maxWidth);

  return (
    <MuiDialog {...dialogProps} open={open} onClose={onClose} TransitionProps={TransitionProps}>
      <TitleRow>
        <TitleGroup>{title}</TitleGroup>
        <IconButton onClick={onClose} aria-label="close" color="primary">
          <CloseOutlined />
        </IconButton>
      </TitleRow>

      <Content dividers>{children}</Content>

      <Actions>{actions}</Actions>
    </MuiDialog>
  );
};

/** Spacing wrapper for a field (or group of fields) inside a `Dialog`/`FormDialog` body. */
export const FieldContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box mb={2} width="100%">
    {children}
  </Box>
);

type ResponsiveDialogProps = {
  fullScreen: boolean;
  fullWidth: boolean;
  maxWidth: MuiDialogProps['maxWidth'];
  TransitionComponent?: typeof Transition;
};

/**
 * Full-screen on phones (where a date picker needs the whole viewport) and a centred dialog from
 * 600px up, rather than taking over a 1920px desktop. The slide-up transition only reads as correct
 * full-screen, so it is spread in conditionally — omitting the prop entirely lets MUI apply its own
 * default on desktop.
 */
const useResponsiveDialogProps = (maxWidth: MuiDialogProps['maxWidth']): ResponsiveDialogProps => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return {
    fullScreen: isMobile,
    fullWidth: true,
    maxWidth,
    ...(isMobile ? { TransitionComponent: Transition } : {}),
  };
};

const TitleRow = styled(DialogTitle)`
  && {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${({ theme }) => theme.spacing(1)};
  }
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
`;

// The extra top padding keeps the first field clear of the header's divider.
const Content = styled(DialogContent)`
  && {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};
    padding-top: ${({ theme }) => theme.spacing(3)};
  }
`;

// Stack the actions on phones so a long submit label is not cramped next to Cancel.
const Actions = styled(DialogActions)`
  && {
    flex-direction: column-reverse;
    align-items: stretch;
    gap: ${({ theme }) => theme.spacing(1)};
    padding: ${({ theme }) => theme.spacing(2)};

    > :not(:first-of-type) {
      margin-left: 0;
    }

    ${({ theme }) => theme.breakpoints.up('sm')} {
      flex-direction: row;
      justify-content: flex-end;
    }
  }
`;
